import './Modal.css';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { getAllUsers } from '../../api/users_institutions';
import { getTopicAdminPermissions } from '../../api/admin_permissions';

const isTempId = (id) => typeof id === 'string' && id.startsWith('temp-');

function ModalTopic({ Action, selectedLevel, selectedTopic, closeModal, stageCreateTopic, stageUpdateTopic, stageDeleteTopic, topicAdminAccess, stageSetTopicAdminAccess }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedAdminUserIds, setSelectedAdminUserIds] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const loadAdminUsers = async () => {
            try {
                const users = await getAllUsers();
                const staffUsers = Array.isArray(users) ? users.filter((user) => user.is_staff && !user.is_superuser) : [];
                if (!cancelled) {
                    setAdminUsers(staffUsers);
                }
            } catch (error) {
                console.error('Error loading admin users:', error);
                if (!cancelled) {
                    setAdminUsers([]);
                }
            }
        };

        loadAdminUsers();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        // A selection already staged this session (Level.jsx's topicAdminAccess map) reflects
        // what the user actually picked -- authoritative over whatever's still in the database,
        // and the only source of truth for a topic created this session (temp id, no DB row yet).
        const stagedSelection = selectedTopic?.id !== undefined ? topicAdminAccess?.[selectedTopic.id] : undefined;
        if (stagedSelection !== undefined) {
            setSelectedAdminUserIds(stagedSelection);
            return;
        }

        setSelectedAdminUserIds([]);

        const loadSelectedTopicAdminAccess = async () => {
            if (!selectedTopic?.id || isTempId(selectedTopic.id)) {
                return;
            }

            try {
                const permissions = await getTopicAdminPermissions();
                const userIds = Array.isArray(permissions)
                    ? permissions
                        .filter((permission) => Number(permission.topic?.id ?? permission.topic) === Number(selectedTopic.id))
                        .map((permission) => permission.user?.id ?? permission.user)
                        .filter((id) => id !== undefined && id !== null)
                    : [];

                if (!cancelled) {
                    setSelectedAdminUserIds(userIds);
                }
            } catch (error) {
                console.error('Error loading topic admin access:', error);
                if (!cancelled) {
                    setSelectedAdminUserIds([]);
                }
            }
        };

        loadSelectedTopicAdminAccess();

        return () => {
            cancelled = true;
        };
    }, [selectedTopic, topicAdminAccess]);

    const toggleAdminAccessBtn = (userId) => {
        setSelectedAdminUserIds((currentIds) =>
            currentIds.includes(userId)
                ? currentIds.filter((currentId) => currentId !== userId)
                : [...currentIds, userId]
        );
    };

    const renderAdminAccessCheckboxes = () => {
        return adminUsers.map((admin) => (
            <button
                key={admin.id}
                type="button"
                className={`access-btn${selectedAdminUserIds.includes(admin.id) ? ' active' : ''}`}
                data-val={admin.username}
                onClick={() => toggleAdminAccessBtn(admin.id)}
            >
                {admin.first_name || admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : admin.username}
            </button>
        ));
    };

    const renderAdminAccessContainer = () => (
        <div id="editModuleAdminAccessContainer" style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
            <label className="label-text">{t('modalTopic.adminAccessLabel')} <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{t('modalTopic.adminAccessHint')}</span></label>
            <div className="access-btn-group" id="editModuleAdminAccessGroup" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {renderAdminAccessCheckboxes()}
            </div>
        </div>
    );

    const handleCreateTopic = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const title = document.getElementById('newTopicTitle').value;
        const is_visible = document.getElementById('newTopicVisibility').checked;
        const topicData = {
            title: title,
            level: selectedLevel?.id || null,
            order: 0,
            is_visible: is_visible,
        };

        try {
            const newTopic = await stageCreateTopic(topicData);
            stageSetTopicAdminAccess(newTopic.id, selectedAdminUserIds);
            console.log('Topic staged:', newTopic);
            closeModal(true);
        } catch (error) {
            console.error('Error staging topic creation:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateTopic = async (topicId) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const title = document.getElementById('editTopicTitle').value;
        const is_visible = document.getElementById('editTopicVisibility').checked;
        const topicData = {
            title: title,
            level: selectedTopic?.level || null,
            is_visible: is_visible,
        };

        try {
            const updatedTopic = await stageUpdateTopic(topicId, topicData);
            stageSetTopicAdminAccess(topicId, selectedAdminUserIds);
            console.log('Topic staged as updated:', updatedTopic);
            closeModal(true);
        } catch (error) {
            console.error('Error staging topic update:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteTopic = async (topicId) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await stageDeleteTopic(topicId);
            console.log('Topic staged as deleted:', topicId);
            closeModal(true);
        } catch (error) {
            console.error('Error staging topic deletion:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
            <div className="Modal">
            {Action === 'Add' ? (
                <div className="modal-box">
                    <h2>{t('modalTopic.addNewTopic')}</h2>
                    <input
                        type="text"
                        id="newTopicTitle"
                        placeholder={t('modalTopic.topicTitlePlaceholder')}
                    />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="newTopicVisibility" defaultChecked={selectedTopic?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={handleCreateTopic} disabled={isSubmitting}>
                            {t('common.create')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Update' ? (
                <div className="modal-box">
                    <h2>{t('modalTopic.editTopic')}</h2>
                    <input
                        type="text"
                        id="editTopicTitle"
                        placeholder={t('modalTopic.topicTitleFieldPlaceholder')}
                        defaultValue={selectedTopic?.title || ''}
                    />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="editTopicVisibility" defaultChecked={selectedTopic?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={() => handleUpdateTopic(selectedTopic.id)} disabled={isSubmitting}>
                            {t('common.change')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Delete' ? (
                <div className="modal-box">
                    <h2>{t('modalTopic.deleteTopic')}</h2>
                    <p>{t('modalTopic.deleteTopicConfirm')}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        <strong>{selectedTopic?.title}</strong>
                    </p>
                    <div className="modal-buttons">
                        <button
                            className="btn-primary btn-delete"
                            style={{ background: 'var(--del-color)' }}
                            onClick={() => handleDeleteTopic(selectedTopic.id)}
                            disabled={isSubmitting}
                        >
                            {t('common.delete')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
        </>
    );
}

export default ModalTopic;
